import { useState } from "react";
import { Eraser, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { usePlatformDesigner } from "@/lib/usePlatformDesigner";
import { usePlatformDesignerBuyerPreview } from "@/lib/usePlatformDesignerBuyerPreview";
import { writeBntAsistenteLastAck } from "@/modules/asistente/bntAsistenteStorage";

const CONFIRM_PHRASE = "BORRAR HISTORIAL";

/**
 * Botón solo diseñador: borra chat equipo, mis tareas y mis notas del workspace.
 * Con ``target`` (cliente seleccionado) purga ese workspace; ``requireTarget`` lo exige.
 */
export default function BntAsistenteClearHistoryButton({
  onCleared,
  target = null,
  requireTarget = false,
}) {
  const isPlatformDesigner = usePlatformDesigner();
  const buyerPreviewActive = usePlatformDesignerBuyerPreview();
  const [open, setOpen] = useState(false);
  const [phrase, setPhrase] = useState("");
  const [busy, setBusy] = useState(false);

  if (!isPlatformDesigner || buyerPreviewActive) return null;

  const targetLabel = target?.label
    || (target?.email ? `${target.name || ""} · ${target.email}`.trim() : "");
  const hasTarget = Boolean(target?.user_id || target?.email || target?.workspace_owner_id);

  const handleOpen = () => {
    if (requireTarget && !hasTarget) {
      toast.error("Seleccione un cliente en la pestaña Equipo antes de borrar el historial.");
      return;
    }
    setOpen(true);
  };

  const handleConfirm = async () => {
    if (phrase.trim().toUpperCase() !== CONFIRM_PHRASE) {
      toast.error(`Escriba exactamente: ${CONFIRM_PHRASE}`);
      return;
    }
    if (requireTarget && !hasTarget) {
      toast.error("Seleccione un cliente para borrar su historial.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        confirm_phrase: CONFIRM_PHRASE,
        scopes: "all",
        require_target: Boolean(requireTarget),
      };
      if (target?.user_id) payload.target_user_id = target.user_id;
      if (target?.email) payload.target_email = target.email;
      if (target?.organization_id) payload.target_organization_id = target.organization_id;
      if (target?.workspace_owner_id) {
        payload.target_workspace_owner_id = target.workspace_owner_id;
      }
      const { data } = await api.post(
        "/asistente/clear-history",
        payload,
        { timeout: 60000 },
      );
      try {
        writeBntAsistenteLastAck({
          alertsCount: 0,
          teamChatGroupCount: 0,
          teamChatPrivateCount: 0,
          teamChatHintCount: 0,
          clientMessagesCount: 0,
          personalNotesDueCount: 0,
          assignedTasksPendingCount: 0,
        });
      } catch {
        /* ignore storage */
      }
      const d = data?.deleted || {};
      toast.success(
        data?.message
          || `Historial borrado (chat ${d.chat_messages || 0}, tareas ${d.tasks || 0}, notas ${d.notes || 0}).`,
      );
      setOpen(false);
      setPhrase("");
      onCleared?.(data);
      // Recarga suave para vaciar paneles montados.
      window.setTimeout(() => {
        window.dispatchEvent(new CustomEvent("bnt-asistente-history-cleared", { detail: data }));
      }, 50);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "No se pudo borrar el historial.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="rounded-sm h-7 text-[11px] border-rose-200 text-rose-900 hover:bg-rose-50"
        onClick={handleOpen}
        title={
          hasTarget
            ? `Borrar historial de ${targetLabel || "cliente seleccionado"}`
            : "Solo diseñador: seleccione un cliente en Equipo y borre su historial"
        }
        data-testid="bnt-asistente-clear-history"
      >
        <Eraser className="h-3 w-3 mr-1" aria-hidden />
        Borrar historial
      </Button>

      <AlertDialog open={open} onOpenChange={(v) => { if (!busy) setOpen(v); }}>
        <AlertDialogContent className="rounded-sm max-w-md" data-testid="bnt-asistente-clear-history-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Borrar historial del asistente</AlertDialogTitle>
            <AlertDialogDescription className="text-xs space-y-2">
              <span className="block">
                Esto elimina de forma permanente en el workspace del cliente:
              </span>
              {targetLabel ? (
                <span
                  className="block font-medium text-slate-900"
                  data-testid="bnt-asistente-clear-history-target-label"
                >
                  {targetLabel}
                </span>
              ) : null}
              <span className="block font-medium text-slate-800">
                Chat de Equipo · Mis tareas · Mis notas
              </span>
              <span className="block">
                Las alertas del asistente se vacían al borrar notas/tareas. No toca alertas
                operativas del Despacho ni la pestaña Clientes.
              </span>
              <span className="block text-rose-800">
                Escriba <strong>{CONFIRM_PHRASE}</strong> para confirmar.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-1.5 py-1">
            <Label htmlFor="bnt-clear-history-phrase" className="text-xs">
              Frase de confirmación
            </Label>
            <Input
              id="bnt-clear-history-phrase"
              value={phrase}
              onChange={(e) => setPhrase(e.target.value)}
              className="rounded-sm h-8 text-sm"
              placeholder={CONFIRM_PHRASE}
              autoComplete="off"
              disabled={busy}
              data-testid="bnt-asistente-clear-history-phrase"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy} className="rounded-sm">
              Cancelar
            </AlertDialogCancel>
            <Button
              type="button"
              variant="destructive"
              className="rounded-sm"
              disabled={busy || phrase.trim().toUpperCase() !== CONFIRM_PHRASE}
              onClick={handleConfirm}
              data-testid="bnt-asistente-clear-history-confirm"
            >
              {busy ? (
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" aria-hidden />
              ) : (
                <Eraser className="h-3.5 w-3.5 mr-1.5" aria-hidden />
              )}
              Borrar ahora
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
